import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { nestApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  InputOTP, InputOTPGroup, InputOTPSlot,
} from "@/components/ui/input-otp";
import { AlertTriangle, Lock, Bell, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const LANGUAGES = [
  { value: "English", label: "English" },
  { value: "Italian", label: "Italiano" },
  { value: "Spanish", label: "Espanol" },
  { value: "French", label: "Francais" },
  { value: "German", label: "Deutsch" },
  { value: "Chinese", label: "Chinese" },
];

const TOGGLEABLE_PUSH = ["push_review", "push_payment", "push_announcement"];

const PUSH_SETTINGS = [
  { key: "push_new_tour", label: "New Tour Requests", desc: "When a new tour offer is available for you", locked: true },
  { key: "push_tour_assigned", label: "Tour Assigned", desc: "When a tour is assigned to you", locked: true },
  { key: "push_tour_cancelled", label: "Tour Cancelled", desc: "When a tour is cancelled", locked: true },
  { key: "push_payment", label: "Payment Processed", desc: "When a payment is confirmed", locked: false },
  { key: "push_review", label: "New Reviews", desc: "When you receive a new customer review", locked: false },
  { key: "push_reminder", label: "Reminders", desc: "Tour reminders and weekly summaries", locked: true },
  { key: "push_announcement", label: "Announcements", desc: "Platform updates and announcements", locked: false },
];

export default function SettingsPage() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { inactive, setInactive } = useOutletContext();
  
  const [settings, setSettings] = useState({
    mark_inactive: false,
    language: "English",
    push_notifications: true,
    push_new_tour: true,
    push_tour_assigned: true,
    push_tour_cancelled: true,
    push_payment: true,
    push_review: true,
    push_reminder: true,
    push_announcement: true,
  });
  const [pendingLanguage, setPendingLanguage] = useState("English");
  const [savingLanguage, setSavingLanguage] = useState(false);
  const [passwords, setPasswords] = useState({ current: "", new_pass: "", confirm: "" });
  
  // Delete account state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deletePassword, setDeletePassword] = useState("");
  const [otp, setOtp] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Password change OTP state
  const [showPasswordOtp, setShowPasswordOtp] = useState(false);
  const [passwordOtp, setPasswordOtp] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  const fetchSettings = useCallback(async () => {
    // Language: localStorage-only for now (no backend)
    const savedLang = localStorage.getItem("language") || "English";
    setSettings((s) => ({ ...s, language: savedLang }));
    setPendingLanguage(savedLang);
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Sync mark_inactive with layout-provided inactive state
  useEffect(() => {
    setSettings((s) => ({ ...s, mark_inactive: !!inactive }));
  }, [inactive]);

  const handleInactiveToggle = async (checked) => {
    setSettings(s => ({ ...s, mark_inactive: checked }));
    setInactive(checked);
    try {
      await nestApi.post("/rahe-portal/account/mark-inactive", { inactive: checked });
      toast.info(
        checked 
          ? t("header.stopReceiving")
          : "You are now active and will receive tour offers"
      );
    } catch {
      toast.error("Failed to update status");
      setSettings(s => ({ ...s, mark_inactive: !checked }));
      setInactive(!checked);
    }
  };

  const handleSaveLanguage = async () => {
    setSavingLanguage(true);
    try {
      // localStorage-only for now (no backend)
      localStorage.setItem("language", pendingLanguage);
      await i18n.changeLanguage(pendingLanguage);
      setSettings((s) => ({ ...s, language: pendingLanguage }));
      toast.success("Language preference saved");
    } catch {
      toast.error("Failed to save language preference");
    } finally {
      setSavingLanguage(false);
    }
  };

  const handlePushMasterToggle = async (checked) => {
    const update = { push_notifications: checked };
    // If disabling master, disable all sub-settings
    if (!checked) {
      PUSH_SETTINGS.forEach(s => { update[s.key] = false; });
    } else {
      PUSH_SETTINGS.forEach(s => { update[s.key] = true; });
    }
    setSettings(s => ({ ...s, ...update }));
    try {
      await nestApi.put("/settings", update);
      toast.success(checked ? "Push notifications enabled" : "Push notifications disabled");
    } catch {
      toast.error("Failed to update notification settings");
    }
  };

  const handlePushToggle = async (key, checked) => {
    setSettings(s => ({ ...s, [key]: checked }));
    try {
      await nestApi.put("/settings", { [key]: checked });
    } catch {
      toast.error("Failed to update setting");
      setSettings(s => ({ ...s, [key]: !checked }));
    }
  };

  const handleRequestPasswordOtp = async () => {
    if (!passwords.current || !passwords.new_pass || !passwords.confirm) {
      toast.error("Please fill in all password fields");
      return;
    }
    if (passwords.new_pass !== passwords.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    if (passwords.new_pass.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    
    setChangingPassword(true);
    try {
      await nestApi.post("/rahe-portal/account/request-password-change", {
        current_password: passwords.current,
      });
      toast.success("Verification code sent to your email");
      setShowPasswordOtp(true);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send verification code");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleVerifyPasswordChange = async () => {
    if (passwordOtp.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }
    
    setChangingPassword(true);
    try {
      await nestApi.post("/rahe-portal/account/verify-password-change", {
        otp: passwordOtp,
        current_password: passwords.current,
        new_password: passwords.new_pass,
      });
      toast.success("Password updated successfully!");
      setShowPasswordOtp(false);
      setPasswords({ current: "", new_pass: "", confirm: "" });
      setPasswordOtp("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid verification code");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleRequestDelete = async () => {
    if (!deletePassword) {
      toast.error("Please enter your current password");
      return;
    }

    setDeleting(true);
    try {
      await nestApi.post("/rahe-portal/account/request-delete-account", {
        current_password: deletePassword,
      });
      toast.success("Verification code sent to your email");
      setDeleteStep(2);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send verification code");
    } finally {
      setDeleting(false);
    }
  };

  const handleVerifyDelete = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }

    setDeleting(true);
    try {
      await nestApi.post("/rahe-portal/account/verify-delete-account", {
        current_password: deletePassword,
        otp,
      });
      toast.success("Account deleted successfully");
      setShowDeleteDialog(false);
      setDeletePassword("");
      setOtp("");
      logout();
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid verification code");
    } finally {
      setDeleting(false);
    }
  };

  const languageChanged = pendingLanguage !== settings.language;

  return (
    <div className="animate-slideUp space-y-6 max-w-4xl" data-testid="settings-page">
      <p className="text-sm text-slate-500">{t("settings.description")}</p>

      {/* Availability */}
      <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-6 space-y-5">
        <h3 className="text-base font-semibold text-slate-900 border-b border-slate-100 pb-2">
          {t("settings.availability")}
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium">{t("settings.markInactive")}</Label>
            <p className="text-xs text-slate-400">{t("settings.stopReceiving")}</p>
          </div>
          <Switch
            checked={settings.mark_inactive}
            onCheckedChange={handleInactiveToggle}
            data-testid="toggle-inactive"
          />
        </div>
      </div>

      {/* Language Preferences */}
      <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-6 space-y-5">
        <h3 className="text-base font-semibold text-slate-900 border-b border-slate-100 pb-2">
          {t("settings.preferences")}
        </h3>
        <div className="max-w-xs">
          <Label>{t("settings.language")}</Label>
          <Select
            value={pendingLanguage}
            onValueChange={setPendingLanguage}
          >
            <SelectTrigger className="mt-1.5" data-testid="settings-language">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGES.map((lang) => (
                <SelectItem key={lang.value} value={lang.value}>{lang.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-400 mt-1">Select a language and save to apply</p>
        </div>
        <Button
          onClick={handleSaveLanguage}
          disabled={!languageChanged || savingLanguage}
          variant={languageChanged ? "default" : "outline"}
          className={languageChanged ? "bg-[#1A1B2E] hover:bg-[#2D2E45]" : ""}
          data-testid="save-language-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {savingLanguage ? "Saving..." : "Save Language"}
        </Button>
      </div>

      {/* Push Notifications */}
      {/* <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-6 space-y-5">
        <h3 className="text-base font-semibold text-slate-900 border-b border-slate-100 pb-2 flex items-center gap-2">
          <Bell className="w-4 h-4" />
          Push Notifications
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium">Enable Push Notifications</Label>
            <p className="text-xs text-slate-400">Master toggle for all push notifications</p>
          </div>
          <Switch
            checked={settings.push_notifications}
            onCheckedChange={handlePushMasterToggle}
            data-testid="toggle-push-master"
          />
        </div>
        
        {settings.push_notifications && (
          <div className="space-y-3 pt-2 border-t border-slate-100">
            {PUSH_SETTINGS.map((item) => (
              <div key={item.key} className="flex items-center justify-between py-1">
                <div>
                  <Label className="text-sm">{item.label}</Label>
                  <p className="text-xs text-slate-400">
                    {item.desc}
                    {item.locked && <span className="ml-1 text-slate-300">(Always on)</span>}
                  </p>
                </div>
                <Switch
                  checked={item.locked ? true : settings[item.key]}
                  onCheckedChange={(checked) => !item.locked && handlePushToggle(item.key, checked)}
                  disabled={item.locked}
                  data-testid={`toggle-${item.key}`}
                  className={item.locked ? "opacity-50" : ""}
                />
              </div>
            ))}
          </div>
        )}
      </div> */}

      {/* Change Password */}
      <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-6 space-y-5">
        <h3 className="text-base font-semibold text-slate-900 border-b border-slate-100 pb-2 flex items-center gap-2">
          <Lock className="w-4 h-4" />
          {t("settings.changePassword")}
        </h3>
        <p className="text-xs text-slate-500">Password change requires email verification for security</p>
        <div className="space-y-4 max-w-md">
          <div>
            <Label>{t("settings.currentPassword")}</Label>
            <Input
              type="password"
              value={passwords.current}
              onChange={(e) => setPasswords((p) => ({ ...p, current: e.target.value }))}
              className="mt-1.5"
              data-testid="current-password"
            />
          </div>
          <div>
            <Label>{t("settings.newPassword")}</Label>
            <Input
              type="password"
              value={passwords.new_pass}
              onChange={(e) => setPasswords((p) => ({ ...p, new_pass: e.target.value }))}
              className="mt-1.5"
              data-testid="new-password"
            />
          </div>
          <div>
            <Label>{t("settings.confirmPassword")}</Label>
            <Input
              type="password"
              value={passwords.confirm}
              onChange={(e) => setPasswords((p) => ({ ...p, confirm: e.target.value }))}
              className="mt-1.5"
              data-testid="confirm-password"
            />
          </div>
        </div>
        <Button
          onClick={handleRequestPasswordOtp}
          disabled={changingPassword}
          variant="outline"
          className="px-8"
          data-testid="change-password-btn"
        >
          {changingPassword ? "Sending code..." : t("settings.changePassword")}
        </Button>
      </div>

      {/* Delete Account */}
      <div className="bg-white border border-red-100 rounded-xl shadow-sm p-6 space-y-5">
        <h3 className="text-base font-semibold text-red-600 border-b border-red-100 pb-2">
          {t("settings.deleteAccount")}
        </h3>
        <p className="text-sm text-slate-600">
          {t("settings.deleteAccountDesc")}
        </p>
        <Button
          variant="destructive"
          onClick={() => {
            setShowDeleteDialog(true);
            setDeleteStep(1);
            setDeletePassword("");
            setOtp("");
          }}
          data-testid="delete-account-btn"
        >
          {t("settings.deleteAccountBtn")}
        </Button>
      </div>

      {/* Password Change OTP Dialog */}
      <Dialog open={showPasswordOtp} onOpenChange={setShowPasswordOtp}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Verify Password Change
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Enter the verification code sent to your email
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Enter the 6-digit code</Label>
              <div className="mt-3 flex justify-center">
                <InputOTP 
                  maxLength={6} 
                  value={passwordOtp} 
                  onChange={setPasswordOtp}
                  data-testid="password-otp-input"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowPasswordOtp(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleVerifyPasswordChange}
                disabled={changingPassword || passwordOtp.length !== 6}
                data-testid="verify-password-btn"
              >
                {changingPassword ? "Changing..." : "Change Password"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {t("deleteAccount.title")}
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {t("deleteAccount.warning")}
            </DialogDescription>
          </DialogHeader>

          {deleteStep === 1 ? (
            <div className="space-y-4 mt-4">
              <div>
                <Label>Enter your current password to confirm</Label>
                <Input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Your current password"
                  className="mt-1.5"
                  data-testid="delete-password-input"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                  {t("deleteAccount.cancel")}
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleRequestDelete}
                  disabled={deleting}
                  data-testid="send-otp-btn"
                >
                  {deleting ? "Sending..." : t("deleteAccount.sendCode")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 mt-4">
              <div>
                <Label>{t("deleteAccount.enterCode")}</Label>
                <div className="mt-3 flex justify-center">
                  <InputOTP 
                    maxLength={6} 
                    value={otp} 
                    onChange={setOtp}
                    data-testid="otp-input"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                  {t("deleteAccount.cancel")}
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleVerifyDelete}
                  disabled={deleting || otp.length !== 6}
                  data-testid="verify-delete-btn"
                >
                  {deleting ? "Deleting..." : t("deleteAccount.verifyAndDelete")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
